import * as Starknet from 'starknet';
import BigNumber from 'bignumber.js';

import { EthereumAddress } from '#/api/types';

import getAccContractAddressAndCallData from '#/features/starknet-wallet/utils/getAccContractAddressAndCallData';
import intNoise from '#/features/starknet-wallet/utils/intNoise';
import toIntString from '#/utils/toIntString';

import { StarknetProvider } from './provider';

export type Address = string;
export interface StarknetSignature {
  r: bigint;
  s: bigint;
}
export type TypedData = Starknet.typedData.TypedData;

interface Account {
  address: Address;
  ethereumAddress: EthereumAddress;
  privateKey: string;
  publicKey: string;
}

interface State {
  account: Account | null;
}

export interface StarknetWallet {
  initializeAccount(
    ethereumAddress: string,
    ethereumSignature: string,
    accountClassHash: string,
    accountProxyClassHash: string,
  ): Promise<void>;
  getAddress(): Account['address'];
  /**
   * @returns Ethereum address used to derive the Starknet address.
   */
  getEthereumAddress(): Account['ethereumAddress'];
  getPublicKey(): Account['publicKey'];
  /**
   * Get the private key of the account. This is intended to be used for
   * displaying the private key to the user. The caller should avoid storing
   * the private key in memory as much as possible.
   *
   * @returns Private key in hex string format, with a leading '0x'.
   */
  getPrivateKey(): Account['privateKey'];
  signTypedData(typedData: TypedData): StarknetSignature;
  callContract(
    provider: StarknetProvider,
    txnInvocation: Starknet.Call | Starknet.Call[],
  ): Promise<Starknet.CallContractResponse>;
  executeTransaction(
    provider: StarknetProvider,
    txnInvocation: Starknet.Call | Starknet.Call[],
  ): Promise<Starknet.InvokeFunctionResponse>;
  getErc20Allowance(
    provider: StarknetProvider,
    erc20TokenAddress: string,
    spenderAddress: string,
  ): Promise<BigNumber>;
  getErc20Balance(
    provider: StarknetProvider,
    erc20TokenAddress: string,
  ): Promise<BigNumber>;
}

const state: State = {
  account: null,
};

const wallet: StarknetWallet = {
  initializeAccount,
  getAddress,
  getEthereumAddress,
  getPublicKey,
  getPrivateKey,
  signTypedData,
  callContract,
  executeTransaction,
  getErc20Allowance,
  getErc20Balance,
};

async function initializeAccount(
  ethereumAddress: string,
  ethereumSignature: string,
  accountClassHash: string,
  accountProxyClassHash: string,
) {
  const StarkwareCryptoUtils = await import(
    '@starkware-industries/starkware-crypto-utils'
  );

  const privateKey =
    StarkwareCryptoUtils.keyDerivation.getPrivateKeyFromEthSignature(
      ethereumSignature,
    );
  const publicKey =
    StarkwareCryptoUtils.keyDerivation.privateToStarkKey(privateKey);
  const { address } = getAccContractAddressAndCallData(
    accountClassHash,
    accountProxyClassHash,
    `0x${publicKey}`,
  );

  try {
    Starknet.validateAndParseAddress(address);
  } catch (err) {
    throw new Error(
      `initializeAccount(): signer address is invalid: '${address}'`,
    );
  }

  state.account = {
    address,
    ethereumAddress,
    privateKey,
    publicKey,
  };
}

function getAddress() {
  if (state.account == null) {
    throw new Error(
      `StarknetWallet should be initialized before calling 'getAddress()'`,
    );
  }

  return state.account.address;
}

function getEthereumAddress() {
  if (state.account == null) {
    throw new Error(
      `StarknetWallet should be initialized before calling 'getEthereumAddress()'`,
    );
  }

  return state.account.ethereumAddress;
}

function getPublicKey() {
  if (state.account == null) {
    throw new Error(
      `StarknetWallet should be initialized before calling 'getPublicKey()'`,
    );
  }

  return state.account.publicKey;
}

function getPrivateKey() {
  if (state.account == null) {
    throw new Error(
      `StarknetWallet should be initialized before calling 'getPrivateKey()'`,
    );
  }
  return `0x${state.account.privateKey}`;
}

function signTypedData(typedData: TypedData) {
  if (state.account == null) {
    throw new Error(
      `StarknetWallet should be initialized before calling 'signTypedData()'`,
    );
  }

  const privateKey = getPrivateKey();
  const msgHash = getMessageHash(typedData);
  const signature = Starknet.ec.starkCurve.sign(msgHash, privateKey);

  return signature;
}

/** Adopted from
 *  @source https://github.com/ConsenSys/starknet-snap/blob/starknet-snap-v0.12.0/packages/starknet-snap/src/utils/starknetUtils.ts#L59
 */
async function callContract(
  provider: StarknetProvider,
  txnInvocation: Starknet.Call,
) {
  if (state.account == null) {
    throw new Error(
      `StarknetWallet should be initialized before calling 'callContract()'`,
    );
  }

  return provider.callContract(txnInvocation, 'latest');
}

/** Adopted from
 *  @source https://github.com/ConsenSys/starknet-snap/blob/starknet-snap-v0.12.0/packages/starknet-snap/src/utils/starknetUtils.ts#L87
 *  @source https://github.com/ConsenSys/starknet-snap/blob/starknet-snap-v0.12.0/packages/starknet-snap/src/sendTransaction.ts
 */
async function executeTransaction(
  provider: StarknetProvider,
  txnInvocation: Starknet.Call | Starknet.Call[],
) {
  if (state.account == null) {
    throw new Error(
      `StarknetWallet should be initialized before calling 'executeTransaction()'`,
    );
  }

  //short term fix for bumping up txn fee
  const MAX_FEE = BigNumber('5e17'); // 5e17 WEI = 0.5 ETH
  const privateKey = getPrivateKey();
  const signerAddress = state.account.address;
  const account = new Starknet.Account(provider, signerAddress, privateKey);
  const abis = undefined;
  const maxFee = MAX_FEE.plus(intNoise(10_000)); // ensure unique txn hash on subsequent calls via `intNoise`
  const details: Starknet.InvocationsDetails = { maxFee: maxFee.toString() };

  return account.execute(txnInvocation, abis, details);
}

async function getErc20Allowance(
  provider: StarknetProvider,
  erc20TokenAddress: string,
  spenderAddress: string,
): Promise<BigNumber> {
  const owner = toIntString(getAddress());
  const spender = toIntString(spenderAddress);

  const response = await callContract(provider, {
    contractAddress: erc20TokenAddress,
    entrypoint: 'allowance',
    calldata: [owner, spender],
  });

  const [result] = response.result;
  if (result == null || BigNumber(result).isNaN()) {
    throw new Error(`Unexpected response reading ERC20 allowance: '${result}'`);
  }

  return BigNumber(result);
}

async function getErc20Balance(
  provider: StarknetProvider,
  erc20TokenAddress: string,
): Promise<BigNumber> {
  const owner = toIntString(getAddress());

  const response = await callContract(provider, {
    contractAddress: erc20TokenAddress,
    entrypoint: 'balanceOf',
    calldata: [owner],
  });

  const [result] = response.result;
  if (result == null || BigNumber(result).isNaN()) {
    throw new Error(`Unexpected response reading ERC20 allowance: '${result}'`);
  }

  return BigNumber(result);
}

function getMessageHash(typedData: TypedData) {
  if (state.account == null) {
    throw new Error(
      `StarknetWallet should be initialized before calling 'getMessageHash()'`,
    );
  }

  return Starknet.typedData.getMessageHash(typedData, state.account.address);
}

export default wallet;
